<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <h1 id="title" class="page-title">{{ labels.title }}</h1>
        <p class="nd-page-kicker">{{ labels.description }}</p>
      </div>
      <v-btn id="btnNew" type="button" color="primary" elevation="0" :to="labels.createRoute">
        <v-icon left small>mdi-plus</v-icon>
        Incluir
      </v-btn>
    </header>

    <module-stat-grid :module="dentistMode ? 'dentists' : 'admins'" :items="dataList.data" :count="dataList.count" />

    <v-card class="nd-section-card">
      <div class="nd-table-toolbar">
        <div>
          <div class="nd-table-toolbar__title">{{ labels.tableTitle }}</div>
          <div class="nd-table-toolbar__subtitle">{{ labels.tableSubtitle }}</div>
        </div>
        <v-form class="nd-table-toolbar__actions nd-table-toolbar__actions--wide" @submit.prevent="load()">
          <v-text-field
            dense
            id="name"
            :placeholder="labels.searchPlaceholder"
            prepend-inner-icon="mdi-magnify"
            v-model="dataList.filter.name" hide-details="auto"
            outlined
          ></v-text-field>
          <v-text-field dense id="email" placeholder="E-mail" v-model="dataList.filter.email" hide-details="auto" outlined></v-text-field>
          <v-btn id="btnFilter" type="submit" outlined color="primary" elevation="0">
            <v-icon left size="18">mdi-filter-outline</v-icon>
            Filtrar
          </v-btn>
        </v-form>
      </div>
            <v-data-table
              :headers="dataList.headers"
              :items="dataList.data"
              @update:items-per-page="pageSizeChanged"
              @update:page="pageChanged"
              :server-items-length="dataList.count"
              :disable-sort="true"
              :loading="dataList.loading"
              :no-data-text="labels.emptyText"
              loading-text="Carregando dados..."
              class="app-table hover-table"
              :options.sync="dataList.options"
              :footer-props="dataList.footerOptions"
            >
              <template v-slot:[`item.name`]="{ item }">
                <div class="nd-entity-cell">
                  <span class="nd-entity-mark">{{ getInitials(item.name) }}</span>
                  <div>
                    <strong>{{ item.name }}</strong>
                    <small>ID {{ item.id }}</small>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="row-actions">
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="viewItem(item)">
                        <v-icon size="19">mdi-pencil-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ labels.editTooltip }}</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="deleteAdmin(item)">
                        <v-icon size="19">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ labels.deleteTooltip }}</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
    </v-card>
  </div>
</template>

<script>
import ModuleStatGrid from "@/components/ModuleStatGrid.vue";
import axios from "axios";

export default {
  components: { ModuleStatGrid },
  name: "AdminList",

  props: {
    dentistMode: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    dataList: {
      filter: {
        name: "",
        email: "",
      },
      headers: [
        { text: "Nome", value: "name" },
        { text: "E-mail", value: "email", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      data: [],
      loading: false,
      count: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["id"],
        sortDesc: [false],
      },
      footerOptions: {
        itemsPerPageText: "Itens por página:",
        itemsPerPageOptions: [10, 20, 30, 40, 50],
        pageText: "{0}-{1} de {2}",
      },
    },
  }),

  created() {
    this.load();
  },

  watch: {
    dentistMode() {
      this.load(true);
    },
  },

  computed: {
    labels() {
      if (this.dentistMode) {
        return {
          title: "Dentistas",
          description: "Profissionais clínicos com acesso ao sistema.",
          tableTitle: "Dentistas",
          tableSubtitle: "Filtre e gerencie dentistas com acesso operacional.",
          searchPlaceholder: "Buscar dentista",
          emptyText: "Nenhum dentista encontrado",
          editTooltip: "Atualizar dentista",
          deleteTooltip: "Remover dentista",
          deleteTitle: "Confirmar exclusão",
          deleteSuccess: "Dentista removido com sucesso!",
          loadError: "Ocorreu um erro ao carregar os dentistas. Tente novamente mais tarde.",
          deleteError: "Ocorreu um erro ao remover o dentista. Tente novamente mais tarde.",
          createRoute: "/dentistas/novo",
          detailsRoute: "/dentistas",
        };
      }

      return {
        title: "Administradores",
        description: "Usuários com acesso operacional à clínica.",
        tableTitle: "Administradores",
        tableSubtitle: "Filtre e gerencie usuários administrativos.",
        searchPlaceholder: "Buscar administrador",
        emptyText: "Nenhum administrador encontrado",
        editTooltip: "Atualizar administrador",
        deleteTooltip: "Remover administrador",
        deleteTitle: "Confirmar exclusão",
        deleteSuccess: "Administrador removido com sucesso!",
        loadError: "Ocorreu um erro ao carregar os administradores. Tente novamente mais tarde.",
        deleteError: "Ocorreu um erro ao remover o administrador. Tente novamente mais tarde.",
        createRoute: "/administradores/novo",
        detailsRoute: "/administradores",
      };
    },

    lastRecordDate() {
      const latest = this.dataList.data.find((item) => item.date || item.created_at);
      const value = latest && (latest.date || latest.created_at);
      return value ? new Date(value).toLocaleDateString("pt-BR") : "-";
    },
  },

  methods: {
    pageChanged(page) {
      this.dataList.options.page = page;
      this.load();
    },

    pageSizeChanged(size) {
      this.dataList.options.itemsPerPage = size;
      this.load(true);
    },

    getInitials(name) {
      if (!name) return "A";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    },

    async load(resetPage = false) {
      if (resetPage) this.dataList.options.page = 1;

      const search = [this.dataList.filter.name, this.dataList.filter.email].filter(Boolean).join(" ");
      const itemsPerPage = this.dataList.options.itemsPerPage || this.dataList.options.size || 10;
      const page = this.dataList.options.page || 1;

      const params = {
        start: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
      };

      if (search) {
        params.search = search;
      }

      if (this.dentistMode) params.dentist = true;

      this.dataList.loading = true;

      try {
        const result = await axios.get("/admin", { params });
        this.dataList.data = result.data.data;
        this.dataList.count = result.data.count;
      } catch (error) {
        this.$dialog.notify.error(
          this.labels.loadError,
          { position: "top-right", timeout: 5000 }
        );
      } finally {
        this.dataList.loading = false;
      }
    },

    viewItem(item) {
      this.$router.push(`${this.labels.detailsRoute}/${item.id}`);
    },

    async deleteAdmin(item) {
      const proceed = await this.$dialog.confirm({
        text: `Deseja realmente remover ${this.dentistMode ? "o dentista" : "o administrador"} "${item.name}"?`,
        title: this.labels.deleteTitle,
        actions: {
          false: "Cancelar",
          true: { text: "Remover", color: "error" },
        },
      });

      if (proceed) {
        this.dataList.loading = true;
        try {
          await axios.delete(`/admin/${item.id}`);
          this.$dialog.notify.info(
            this.labels.deleteSuccess,
            { position: "top-right", timeout: 2000 }
          );
          this.load();
        } catch (error) {
          this.$dialog.notify.error(
            this.labels.deleteError,
            { position: "top-right", timeout: 5000 }
          );
        } finally {
          this.dataList.loading = false;
        }
      }
    },
  },
};
</script>
